import {addMinutes, subMinutes} from 'date-fns'
import {set} from 'object-path-immutable'
import React, {useCallback, useState} from 'react'
import styled from 'styled-components'
import {useDebouncedCallback} from 'use-debounce'
import {makeSampleUserProfile} from '../../../common/sample-formats/end-user'
import {
  DynamicVariableScript,
  ScriptContext,
  VariablesRecord
} from '../../../common/types/variable-engine'
import {evaluateVariable} from '../../../common/variable-engine'
import {Button} from '../../../components/library/buttons'
import {
  DropdownItem,
  DropdownItemSmall
} from '../../../components/library/dropdown/content'
import {Dropdown} from '../../../components/library/dropdown/dropdown'
import {MultilineInput} from '../../../components/library/form'
import {Icon} from '../../../components/library/icon/icon'
import {EnvironmentNode} from '../../../environment'
import {useCustomProfileFields} from '../../../hooks/use-custom-profile-fields'
import {useVariables} from '../../../providers/variables/provider'
import {VariableAction} from './actions'
import {storeDefaultInSession} from './helpers'

const dummyScriptContext: ScriptContext = {
  cardInstance: {
    publishedAt: subMinutes(new Date(), 15).toISOString(),
    expires: addMinutes(new Date(), 25).toISOString(),
    firstSeen: subMinutes(new Date(), 5).toISOString(),
    priority: 5,
    isTest: true
  }
}

type ScriptFormProps = {
  environment: EnvironmentNode
  activeVariable: DynamicVariableScript
  dispatch: React.Dispatch<VariableAction>
  varName: string
  readOnly?: boolean
  providerId?: string
}
const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

const Help = styled.div`
  display: flex;
  gap: 10px;
  padding: 10px;
  background-color: ${props => props.theme.grey02};
  border-radius: ${props => props.theme.borderRadius};

  svg {
    margin-top: 5px;
    flex-shrink: 0;
    color: ${props => props.theme.grey65};
  }
  p {
    font-size: 14px;
    color: ${props => props.theme.grey80};
    margin-top: 10px;
  }
  p:first-child {
    margin-top: 0;
  }
  pre {
    display: inline;
    color: ${props => props.theme.blue};
  }
`

const scriptPresets = [
  {
    varNameDefault: 'sentMinsAgo',
    name: 'Card published time (minutes ago)',
    script: `const sent = new Date(cardInstance.publishedAt)
const now = new Date()

const sentAgo = now.getTime() - sent.getTime()
const sentMinsAgo = sentAgo / 1000 / 60

return Math.round(sentMinsAgo)`
  },
  {
    varNameDefault: 'expiresInMins',
    name: 'Card expiry time (minutes from now)',
    script: `const expires = new Date(cardInstance.expires)
const now = new Date()

const expiresIn = expires.getTime() - now.getTime()
const expiresInMins = expiresIn / 1000 / 60

return Math.max(0, Math.round(expiresInMins))`
  }
]

export const ScriptForm = ({
  environment,
  varName,
  dispatch,
  activeVariable,
  readOnly,
  providerId
}: ScriptFormProps) => {
  const variables = useVariables()

  const [customProfileFields] = useCustomProfileFields(environment.id)
  const profileCustomFields = customProfileFields.reduce(
    (acc, {name}) => ({
      ...acc,
      [name]: `<${name}>`
    }),
    {}
  )
  const profile = makeSampleUserProfile(profileCustomFields)

  const evaluate = useCallback(
    (vars: VariablesRecord) =>
      evaluateVariable(
        varName,
        {},
        vars,
        {},
        profile,
        {timezone},
        true,
        dummyScriptContext
      ),
    [profile, varName]
  )

  const [preview, setPreview] = useState<string>(() => {
    const evaluated = evaluate(variables)
    return evaluated ? JSON.stringify(evaluated, null, 2) : ''
  })

  const evaluateDebounced = useDebouncedCallback((script: string) => {
    const updatedVariables = set(
      variables,
      varName,
      set(activeVariable, 'script', script)
    )
    const evaluated = evaluate(updatedVariables)
    setPreview(evaluated ? JSON.stringify(evaluated, null, 2) : '')
  }, 500)

  return (
    <>
      <MultilineInput
        label="Enter script..."
        upLabel="Script"
        value={activeVariable.script}
        onChange={value => {
          storeDefaultInSession('script', varName, value, providerId)
          dispatch({type: 'setScript', payload: value})
          evaluateDebounced(value)
        }}
        inputStyle={{
          fontFamily: 'atomic___SanFranciscoMono, monospace',
          fontSize: '0.8em'
        }}
        readOnly={readOnly}
      />
      <Dropdown
        order="up" // increase z-index because in modal, also allows click away closing
        trigger={(handleClick, ref) => (
          <Button
            stretch="center"
            appearance="secondary"
            ref={ref as React.Ref<HTMLButtonElement>}
            isRounded={true}
            onClick={handleClick}
            size="small"
            iconRight="chevron"
            rotateIcon={90}
            weight="light"
          >
            <label>or choose a preset</label>
          </Button>
        )}
      >
        {closeDropdown =>
          scriptPresets.map(({name, script, varNameDefault}) => (
            <DropdownItemSmall key={name}>
              <DropdownItem>
                <a
                  onClick={() => {
                    if (!varName) {
                      dispatch({type: 'setCustomName', payload: varNameDefault})
                    }
                    storeDefaultInSession(
                      'script',
                      varName ?? varNameDefault,
                      script,
                      providerId
                    )
                    dispatch({type: 'setScript', payload: script})
                    evaluateDebounced(script)
                    closeDropdown()
                  }}
                >
                  <span>{name}</span>
                </a>
              </DropdownItem>
            </DropdownItemSmall>
          ))
        }
      </Dropdown>
      <Help>
        <Icon name="info" />
        <div>
          <p>
            Script variables evaluate JavaScript expressions during card
            assembly.
          </p>
          <p>
            To reference existing variables use the keyword <pre>variables</pre>
            . For example <pre>variables.foo</pre>.
          </p>
          <p>
            Use the keyword <pre>profile</pre> to reference profile variables.
            For example <pre>profile.name</pre>.
          </p>
          <p>
            Use the keyword <pre>cardInstance</pre> to reference data about the
            card that the variable is in. For example{' '}
            <pre>cardInstance.publishedAt</pre>.
          </p>
        </div>
      </Help>
      <MultilineInput
        label="Script evaluation preview"
        value={preview}
        onChange={() => undefined}
        inputStyle={{
          fontFamily: 'atomic___SanFranciscoMono, monospace',
          fontSize: '0.8em'
        }}
        readOnly={true}
        disabled={true}
      />
    </>
  )
}
